import React, { useRef, useState } from 'react';
import { Box, Typography, IconButton, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';

const PRIMARY = '#f25d27';

/**
 * Deduplicar por name + size + lastModified.
 * @param {File[]} existing
 * @param {File[]} incoming
 * @returns {File[]}
 */
function deduplicateFiles(existing, incoming) {
  const keys = new Set(existing.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
  const merged = [...existing];
  for (const file of incoming) {
    const key = `${file.name}-${file.size}-${file.lastModified}`;
    if (!keys.has(key)) {
      keys.add(key);
      merged.push(file);
    }
  }
  return merged;
}

/**
 * @param {{
 *   files: File[];
 *   onFilesChange: (files: File[]) => void;
 * }} props
 */
export default function SupportCreateAttachmentDropzone({ files, onFilesChange }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  function handleFiles(incoming) {
    const arr = Array.from(incoming || []);
    onFilesChange(deduplicateFiles(files, arr));
    if (inputRef.current) inputRef.current.value = '';
  }

  function handleDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }

  function handleDragLeave() {
    setDragging(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }

  function removeFile(index) {
    onFilesChange(files.filter((_, i) => i !== index));
  }

  return (
    <Box>
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: `2px dashed ${dragging ? PRIMARY : '#d0d0d0'}`,
          borderRadius: '8px',
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          background: dragging ? '#fff8f5' : '#fafafa',
          transition: 'border-color 0.15s, background 0.15s',
          '&:hover': { borderColor: PRIMARY, background: '#fff8f5' },
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: '2rem', color: dragging ? PRIMARY : '#ccc', mb: 0.5 }} />
        <Typography sx={{ fontSize: '0.85rem', color: '#888' }}>
          Arraste e solte arquivos aqui
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#bbb' }}>
          ou clique para selecionar
        </Typography>
      </Box>

      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
      />

      {files.length > 0 && (
        <List dense disablePadding sx={{ mt: 1 }}>
          {files.map((file, i) => (
            <ListItem
              key={`${file.name}-${i}`}
              disablePadding
              sx={{
                border: '1px solid #eee',
                borderRadius: '6px',
                mb: 0.5,
                px: 1,
                py: 0.25,
                background: '#fafafa',
              }}
              secondaryAction={
                <IconButton size="small" onClick={() => removeFile(i)} title="Remover anexo">
                  <DeleteOutlineIcon sx={{ fontSize: '1rem', color: '#d32f2f' }} />
                </IconButton>
              }
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <AttachFileIcon sx={{ fontSize: '0.9rem', color: '#888' }} />
              </ListItemIcon>
              <ListItemText
                primary={file.name}
                primaryTypographyProps={{ fontSize: '0.82rem', color: '#333', noWrap: true }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
